import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "./ui/dialog";
import { splitTitle } from "./Services";

const DEFAULT_PHOTOS = [
  { src: "https://images.unsplash.com/photo-1727893119356-1702fe921cf9?crop=entropy&cs=srgb&fm=jpg&q=85", caption: "BAY 03 /// ENGINE BUILD" },
  { src: "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?crop=entropy&cs=srgb&fm=jpg&q=85", caption: "MASTER TECH AT WORK" },
  { src: "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&q=85", caption: "PRECISION MEASUREMENT" },
  { src: "https://images.unsplash.com/photo-1429772011165-0c2e054367b8?crop=entropy&cs=srgb&fm=jpg&q=85", caption: "ENGINE DETAIL" },
  { src: "https://images.unsplash.com/photo-1617814076367-b759c7d7e738?crop=entropy&cs=srgb&fm=jpg&q=85", caption: "AMG GT /// DELIVERY DAY" },
  { src: "https://images.unsplash.com/photo-1714434087840-c176041f0291?crop=entropy&cs=srgb&fm=jpg&q=85", caption: "M5 /// FRESH OFF THE DYNO" },
];

export default function Gallery({ content }) {
  const [active, setActive] = useState(null);
  const items = content?.gallery?.length ? content.gallery : DEFAULT_PHOTOS;
  const eyebrow = content?.gallery_eyebrow || "05 /// THE FLOOR, UP CLOSE";
  const [titleA, titleB] = splitTitle(content?.gallery_title || "Inside the Haus");

  return (
    <section id="gallery" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="gallery-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-crimson font-semibold mb-4">{eyebrow}</p>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-100">
          {titleA} <span className="text-outline">{titleB}</span>
        </h2>
      </motion.div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[240px]" data-testid="gallery-grid">
        {items.map((p, i) => (
          <motion.button
            key={`${p.src}-${i}`}
            data-testid={`gallery-item-${i}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => setActive(p)}
            className={`group relative overflow-hidden border border-[#263342] hover:border-crimson/50 transition-colors text-left ${i === 0 ? "sm:col-span-2 sm:row-span-2" : ""}`}
          >
            <img
              src={p.src}
              alt={p.caption || "Workshop photo"}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12]/85 via-transparent to-transparent opacity-80" />
            {p.caption && (
              <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-200 bg-[#0A0D12]/70 backdrop-blur px-3 py-1.5 border border-[#263342]">
                {p.caption}
              </span>
            )}
          </motion.button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-[#0A0D12] border-[#263342] max-w-5xl w-[95vw] p-2 sm:p-3" data-testid="gallery-lightbox">
          {active && (
            <>
              <img src={active.src} alt={active.caption || "Workshop photo"} className="w-full max-h-[80vh] object-contain" />
              {active.caption && (
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 text-center py-3">{active.caption}</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
